import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import {
  formatHttpErrorDetails,
  formatThrownErrorDetails,
  friendlyNetworkErrorMessage,
} from '../api/httpErrorFormat';
import { persistStoredRefreshToken } from '../auth/sessionToken';
import { clearRefreshUnavailable404 } from '../api/authRefresh';
import { useI18n } from '../i18n';
import { localizeServerMessage } from '../i18n/localizeServerMessage';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { persistIdentifierForLoginOption } from './lastLoginStorage';
import { persistLastSignInMethod } from './lastSignInMethod';
import { serverRoleMatchesLoginType } from './authEntryPolicy';
import type { LoginOption } from './loginConstants';
import {
  appleLoginDebugRequestPayload,
  appleProfileFromCredential,
  enrichAuthBodyWithAppleProfile,
  extractRefreshTokenFromAppleLogin,
  friendlyAppleLoginFailureSummary,
  isAppleSignInCancelled,
  isAppleUserMissingError,
  isSuccessfulAppleLoginResult,
  postAppleLoginCredential,
  postAppleLoginCredentialOnly,
  requestAppleWebSignIn,
  takeAppleCallbackFromWindow,
  takeAppleSignInPending,
  loginTypeFromAppleOAuthState,
  appleOAuthStateNonce,
  resolveAppleClientId,
  resolveAppleRedirectUri,
  type AppleFirstSignInUser,
  type AppleIdTokenProfile,
} from './appleSignIn';
import { roleIdFromJwt, profileRoleIdFromData } from '../auth/sessionBookingRole';
import { translateLoginOption } from '../i18n';
import {
  isNativeAppleSignInAvailable,
  isNativeAppleSignInCancelled,
  requestNativeAppleIdToken,
} from './nativeAppleSignIn';

export type UseAppleSignInOptions = {
  /** On register pages: sign in existing users; prefill the form when the account is new. */
  registerPrefillOnMiss?: boolean;
  onRegisterPrefill?: (profile: AppleIdTokenProfile, credential: string) => void;
};

export function useAppleSignIn(loginType: LoginOption, options: UseAppleSignInOptions = {}) {
  const { t } = useI18n();
  const { applyLoginSuccess, isLoggedIn } = useAuth();
  const { setView } = useAppNavigation();
  const clientId = resolveAppleClientId();
  const redirectUri = resolveAppleRedirectUri();
  const isConfigured = Boolean(clientId && redirectUri);
  const [submitting, setSubmitting] = useState(false);
  const [errorFriendly, setErrorFriendly] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);

  const { registerPrefillOnMiss = false, onRegisterPrefill } = options;

  const completeAppleCredential = useCallback(
    async (
      credential: string,
      firstSignInUser?: AppleFirstSignInUser | null,
      type: LoginOption = loginType,
    ) => {
      if (isLoggedIn) {
        return;
      }
      setSubmitting(true);
      setErrorFriendly(null);
      setErrorDetails(null);
      try {
        const result = registerPrefillOnMiss
          ? await postAppleLoginCredentialOnly(credential, type, firstSignInUser)
          : await postAppleLoginCredential(credential, type, firstSignInUser);
        if (isSuccessfulAppleLoginResult(result)) {
          const tokenRoleId =
            roleIdFromJwt(result.accessToken) ?? profileRoleIdFromData(result.parsed);
          if (!serverRoleMatchesLoginType(type, tokenRoleId)) {
            setErrorFriendly(
              t('auth.portal.appleWrongServerRole', {
                roleId: String(tokenRoleId ?? '?'),
                type: translateLoginOption(type, t),
              }),
            );
            setErrorDetails(
              formatHttpErrorDetails({
                requestUrl: result.urlUsed,
                requestPayload: appleLoginDebugRequestPayload(
                  credential,
                  type,
                  result,
                  firstSignInUser,
                ),
                response: result.response,
                responseText: result.responseText,
              }),
            );
            return;
          }
          const appleProfile = appleProfileFromCredential(credential, firstSignInUser);
          const email = appleProfile.email;
          const loginIdentifier = email || 'apple-user';
          const enrichedBody = enrichAuthBodyWithAppleProfile(result.parsed, appleProfile);
          applyLoginSuccess(enrichedBody, result.accessToken!, loginIdentifier, type);
          persistIdentifierForLoginOption(type, loginIdentifier);
          persistLastSignInMethod('apple');
          const refreshToken = extractRefreshTokenFromAppleLogin(result.parsed);
          if (refreshToken) {
            persistStoredRefreshToken(refreshToken);
          }
          clearRefreshUnavailable404();
          setView('home', { replace: true });
          return;
        }
        if (registerPrefillOnMiss && isAppleUserMissingError(result)) {
          const appleProfile = appleProfileFromCredential(credential, firstSignInUser);
          onRegisterPrefill?.(appleProfile, credential);
          return;
        }
        setErrorFriendly(
          localizeServerMessage(
            friendlyAppleLoginFailureSummary(result, {
              notEnabled: t('auth.portal.appleNotEnabled'),
              unavailable: t('auth.portal.appleUnavailable'),
              loginTypeMismatch: t('auth.portal.appleLoginTypeMismatch'),
              userNotRegistered: t('auth.portal.appleUserNotRegistered'),
              missingToken: t('auth.portal.appleMissingToken'),
              invalidToken: t('auth.portal.appleInvalidToken'),
            }),
            t,
          ),
        );
        setErrorDetails(
          formatHttpErrorDetails({
            requestUrl: result.urlUsed,
            requestPayload: appleLoginDebugRequestPayload(
              credential,
              type,
              result,
              firstSignInUser,
            ),
            response: result.response,
            responseText: result.responseText,
          }),
        );
      } catch (err) {
        setErrorFriendly(friendlyNetworkErrorMessage(err));
        setErrorDetails(formatThrownErrorDetails(err));
      } finally {
        setSubmitting(false);
      }
    },
    [applyLoginSuccess, isLoggedIn, loginType, onRegisterPrefill, registerPrefillOnMiss, setView, t],
  );

  useEffect(() => {
    if (isLoggedIn) {
      return;
    }
    const callback = takeAppleCallbackFromWindow();
    if (!callback) {
      return;
    }
    const pending = takeAppleSignInPending();
    if (callback.error) {
      setErrorFriendly(t('auth.portal.appleLoadFailed'));
      setErrorDetails(callback.error);
      return;
    }
    if (!callback.idToken) {
      return;
    }
    if (pending?.state && callback.state) {
      const pendingNonce = appleOAuthStateNonce(pending.state);
      const callbackNonce = appleOAuthStateNonce(callback.state);
      if (pendingNonce && callbackNonce && pendingNonce !== callbackNonce) {
        setErrorFriendly(t('auth.portal.appleLoadFailed'));
        return;
      }
    }
    const type =
      pending?.loginType ?? loginTypeFromAppleOAuthState(callback.state) ?? loginType;
    void completeAppleCredential(callback.idToken, callback.user, type);
  }, [completeAppleCredential, isLoggedIn, loginType, t]);

  const requestAppleSignIn = useCallback(async () => {
    if (!clientId || !redirectUri || isLoggedIn || submitting) {
      return;
    }
    setErrorFriendly(null);
    setErrorDetails(null);
    setSubmitting(true);
    try {
      // iOS Capacitor: native ASAuthorization only. Web appleid.apple.com is Guideline 4.0.
      if (isNativeAppleSignInAvailable()) {
        const native = await requestNativeAppleIdToken();
        await completeAppleCredential(native.idToken, native.user);
        return;
      }
      const web = await requestAppleWebSignIn(clientId, redirectUri, loginType);
      await completeAppleCredential(web.idToken, web.user);
    } catch (err) {
      if (!isNativeAppleSignInCancelled(err) && !isAppleSignInCancelled(err)) {
        setErrorFriendly(t('auth.portal.appleLoadFailed'));
        setErrorDetails(formatThrownErrorDetails(err));
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    clientId,
    completeAppleCredential,
    isLoggedIn,
    loginType,
    redirectUri,
    submitting,
    t,
  ]);

  return {
    clientId,
    redirectUri,
    isConfigured,
    submitting,
    errorFriendly,
    errorDetails,
    requestAppleSignIn,
    clearErrors: useCallback(() => {
      setErrorFriendly(null);
      setErrorDetails(null);
    }, []),
  };
}
